/**
 * Advanced Search Config Adapter for Manufacturers
 * Transforms Manufacturer Field Registry into Advanced Search field configurations
 */

import { FieldConfig } from '../../../components/TableSearch/types';
import { MANUFACTURER_FIELDS_REGISTRY } from '../fields';
import { BaseFieldDefinition } from '../../../shared/types/fieldRegistry';
import {
  getContextAwareConfigFromRegistry,
  toStandardAsyncSelectConfig,
} from '../../../shared/utils/contextAwareAsyncSelectConfig';

/**
 * Convert a field definition to Advanced Search field config
 * Handles flattening of relational fields for searching
 */
function toSearchFieldConfig(
  field: BaseFieldDefinition
): FieldConfig | FieldConfig[] | null {
  // Skip non-searchable fields
  if (!field.isSearchable) {
    return null;
  }

  // Handle relational fields - use async select for API-based relations
  if (field.isRelation && field.relationConfig) {
    const { displayField } = field.relationConfig;

    const configs: FieldConfig[] = [];

    // Get context-aware config for search context
    // Search context uses searchField (name) for value (query matching)
    const contextConfig = getContextAwareConfigFromRegistry(
      MANUFACTURER_FIELDS_REGISTRY,
      field.key as string,
      'search' // ✅ Search context uses name field
    );

    if (contextConfig) {
      // Convert to standard config for component props
      const asyncConfig = toStandardAsyncSelectConfig(contextConfig);

      configs.push({
        key: displayField,
        label: field.label,
        dataType: 'selection', // Use selection with async config
        apiEndpoint: asyncConfig.apiEndpoint,
        searchField: asyncConfig.searchField,
        valueField: asyncConfig.valueField, // Uses name field for query matching
        labelField: asyncConfig.labelField,
      });
    } else {
      // Fallback to string search if no async config
      configs.push({
        key: displayField,
        label: field.label,
        dataType: 'string',
      });
    }

    return configs;
  }

  // Standard fields - map directly. A field with a real finite value set
  // (selectionOptions) always renders as a dropdown, regardless of its
  // underlying dataType — previously only dataType === 'boolean' fields
  // got this, so string fields with a defined enum (lgm_visibility,
  // vendor_type, source_of_record, lei_entity_status,
  // lei_registration_status) fell through to a free-text input with their
  // options silently unused.
  const dataType = field.selectionOptions
    ? 'selection'
    : field.dataType === 'object'
      ? 'string'
      : (field.dataType as 'string' | 'number' | 'date' | 'selection');

  return {
    key: field.key as string,
    label: field.label,
    dataType,
    options: field.selectionOptions,
  };
}

/**
 * Async-select config for filtering Manufacturers by a specific manufacturer
 * (manufacturer-product-linking, Task 4 follow-up). Not derived from
 * MANUFACTURER_FIELDS_REGISTRY -- a manufacturer has no "manufacturer"
 * relation field to itself -- so this is hand-written, same as Products'
 * own `gradeIdConfig` in its searchConfigAdapter (grades has no self-relation
 * either). Hides the raw id from the user: the picker searches/displays by
 * manufacturer_name and submits manufacturer_id underneath. Used by the
 * "View Manufacturer" deep link (Task 4) to seed a rule that the advanced
 * search panel can actually render/edit, and gives the collapsed filter
 * summary a real "Manufacturer" label instead of the raw field key.
 */
const MANUFACTURER_ID_CONFIG: FieldConfig = {
  key: 'manufacturer_id',
  label: 'Manufacturer',
  dataType: 'selection',
  apiEndpoint: '/manufacturers',
  searchField: 'manufacturer_name',
  valueField: 'manufacturer_id',
  labelField: 'manufacturer_name',
};

/**
 * Async-select config for the "Parent Manufacturer" filter. Same
 * apiEndpoint/searchField/valueField/labelField shape as
 * MANUFACTURER_ID_CONFIG above, but scoped server-side via `extraParams`
 * to manufacturers that are actually a parent today (some other
 * manufacturer's parent_manufacturer_id points at them) — a user should
 * never have to know/manage the raw id, and shouldn't be offered every
 * active manufacturer as a candidate "parent" either. Replaces
 * `parent_manufacturer_id`'s own registry entry in Advanced Search (that
 * entry is `isSearchable: false` for exactly this reason — see fields.ts).
 */
const PARENT_MANUFACTURER_ID_CONFIG: FieldConfig = {
  key: 'parent_manufacturer_id',
  label: 'Parent Manufacturer',
  dataType: 'selection',
  apiEndpoint: '/manufacturers',
  searchField: 'manufacturer_name',
  valueField: 'manufacturer_id',
  labelField: 'manufacturer_name',
  extraParams: { isKnownParent: 'true' },
};

/**
 * Convert Manufacturer Field Registry to Advanced Search field configurations
 * @returns Array of FieldConfig for Advanced Search component
 */
export function toAdvancedSearchConfig(): FieldConfig[] {
  const configs: FieldConfig[] = [];

  MANUFACTURER_FIELDS_REGISTRY.forEach(field => {
    const config = toSearchFieldConfig(field);
    if (config) {
      if (Array.isArray(config)) {
        configs.push(...config);
      } else {
        configs.push(config);
      }
    }
  });

  configs.push(MANUFACTURER_ID_CONFIG);
  configs.push(PARENT_MANUFACTURER_ID_CONFIG);

  return configs;
}

/**
 * Get searchable field keys (useful for validation)
 */
export function getSearchableFieldKeys(): string[] {
  return toAdvancedSearchConfig().map(config => config.key);
}
